// src/routeTemplates/hooks/useRouteTemplatesCoreData/useRouteTemplatesCoreData.ts
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { gql, useQuery } from "@apollo/client";
import { useDebounce } from "usehooks-ts";
import { useAuth } from "@src/common/hooks/useAuth";
import {
  LabelFragmentFragmentDoc,
  RouteTemplatesSortField,
  SortingDirection,
} from "@api/graphql/generated/generated-types";
import { RouteTemplateSummary } from "../../types/routeTemplateTypes";
import type { SelectedItem } from "../../context/RouteTemplatesFiltersContext";
import { formatRouteTemplatesResponse } from "../../utils/routeTemplateFormatters";
import { RouteTemplatesFiltersContext } from "../../context/RouteTemplatesFiltersContext";

const ROUTE_TEMPLATES_PAGINATED = gql`
  query RouteTemplatesPaginated(
    $courierId: ID!
    $limit: Int
    $searchTerm: String
    $driverIds: [ID!]
    $endCustomerIds: [ID!]
    $orderLabelIds: [ID!]
    $sortFields: [RouteTemplatesSorting!]
  ) {
    routeTemplatesPaginated(
      courierId: $courierId
      limit: $limit
      searchTerm: $searchTerm
      driverIds: $driverIds
      endCustomerIds: $endCustomerIds
      orderLabelIds: $orderLabelIds
      sortFields: $sortFields
    ) {
      edges {
        node {
          id
          name
          displayId
          enabledCommunicationTypes
          stopTemplatesInSequence {
            stopTemplateId
            sequenceNumber
            stopTemplate {
              shipmentTemplateId
              streetAddress
              city
              companyName
              contactName
              type
              timeWindow {
                openSecondsSinceMidnightUtc
                closeSecondsSinceMidnightUtc
              }
              lngLat {
                lng
                lat
              }
              packageMovements {
                id
                displayId
                name
              }
              shipmentTemplate {
                order {
                  displayId
                  endCustomer {
                    id
                    companyName
                  }
                  recurrenceDetail {
                    scheduleType
                    weeklySchedule {
                      weekDays
                    }
                  }
                  serviceType {
                    name
                    color
                  }
                }
              }
            }
          }
          schedule {
            schedules {
              driverId
              driver {
                id
                firstName
                lastName
                photoUrl
                avatarDisplay
                assignedLabels {
                  ...LabelFragment
                }
              }
              detail {
                scheduleType
                startDateISO8601
                endDateISO8601
                weeklySchedule {
                  weekDays
                }
                biweeklySchedule {
                  startDateISO8601
                }
                monthlySchedule {
                  days
                }
              }
            }
          }
        }
      }
    }
  }
  ${LabelFragmentFragmentDoc}
`;

export const useRouteTemplatesCoreData = (skip = false, searchTerm?: string): {
  routes: RouteTemplateSummary[];
  availableEndCustomers: SelectedItem[];
  isLoading: boolean;
  refetch: () => void;
} => {
  const { courierId } = useAuth();

  // Optional: gracefully degrade when called outside RouteTemplatesFiltersProvider
  // (e.g. AddToRouteModal, BulkAssignToLiveRouteModal)
  const filtersContext = useContext(RouteTemplatesFiltersContext);
  const rawSearchTerm = filtersContext?.searchTerm ?? "";
  const driverIds = filtersContext?.driverIds ?? [];
  const endCustomerIds = (filtersContext?.selectedEndCustomers ?? []).map(
    (c) => c.id
  );
  const orderLabelIds = filtersContext?.orderLabelIds ?? [];
  const sortDirection = filtersContext?.sortDirection ?? SortingDirection.Asc;

  const debouncedSearchTerm = useDebounce(rawSearchTerm, 300);

  const { data, loading, refetch } = useQuery(ROUTE_TEMPLATES_PAGINATED, {
    variables: {
      courierId,
      limit: 500,
      searchTerm: searchTerm || debouncedSearchTerm || undefined,
      driverIds: driverIds.length ? driverIds : undefined,
      endCustomerIds: endCustomerIds.length ? endCustomerIds : undefined,
      orderLabelIds: orderLabelIds.length ? orderLabelIds : undefined,
      sortFields: [
        { field: RouteTemplatesSortField.Name, direction: sortDirection },
      ],
    },
    fetchPolicy: "cache-and-network",
    skip: skip || !courierId,
  });

  const routes = useMemo(() => formatRouteTemplatesResponse(data), [data]);

  // Accumulate unique customers across all fetched routes so the customer
  // dropdown always shows the full set even when other filters narrow the list.
  const seenRef = useRef(new Map<string, SelectedItem>());
  const [availableEndCustomers, setAvailableEndCustomers] = useState<SelectedItem[]>([]);

  useEffect(() => {
    let changed = false;
    for (const route of routes) {
      for (const stop of route.stops) {
        if (stop.endCustomerId && !seenRef.current.has(stop.endCustomerId)) {
          seenRef.current.set(stop.endCustomerId, {
            id: stop.endCustomerId,
            label: stop.endCustomerName ?? stop.endCustomerId,
          });
          changed = true;
        }
      }
    }
    if (changed) {
      setAvailableEndCustomers(
        Array.from(seenRef.current.values()).sort((a, b) =>
          a.label.localeCompare(b.label)
        )
      );
    }
  }, [routes]);

  return { routes, availableEndCustomers, isLoading: loading, refetch };
};
